import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Baby, Edit2, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface BabyProfileSetupProps {
  onBabySelect: (babyId: string) => void;
}

export function BabyProfileSetup({ onBabySelect }: BabyProfileSetupProps) {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<any>(null);
  const [deleteProfile, setDeleteProfile] = useState<any>(null);
  const [babyName, setBabyName] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from("baby_profiles")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (!error && data) {
      setProfiles(data);
      if (data.length > 0) {
        onBabySelect(data[0].id);
      }
    }
  };

  const handleCreateProfile = async () => {
    if (!babyName) {
      toast({
        title: "Name required",
        description: "Please enter a name for your baby",
        variant: "destructive",
      });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    if (editingProfile) {
      const { error } = await supabase
        .from("baby_profiles")
        .update({
          baby_name: babyName,
          due_date: dueDate || null,
          birth_date: birthDate || null,
        })
        .eq("id", editingProfile.id);

      if (error) {
        toast({ title: "Error", description: "Failed to update profile", variant: "destructive" });
        return;
      }

      toast({ title: "Updated!", description: "Baby profile has been updated" });
    } else {
      const { data, error } = await supabase
        .from("baby_profiles")
        .insert({
          user_id: user.id,
          baby_name: babyName,
          due_date: dueDate || null,
          birth_date: birthDate || null,
        })
        .select()
        .single();

      if (error) {
        toast({ title: "Error", description: "Failed to create profile", variant: "destructive" });
        return;
      }

      toast({ title: "Profile created!", description: "Baby profile has been created successfully" });
      if (data) onBabySelect(data.id);
    }

    setIsDialogOpen(false);
    setEditingProfile(null);
    setBabyName("");
    setDueDate("");
    setBirthDate("");
    loadProfiles();
  };

  const handleEditProfile = (profile: any) => {
    setEditingProfile(profile);
    setBabyName(profile.baby_name);
    setDueDate(profile.due_date || "");
    setBirthDate(profile.birth_date || "");
    setIsDialogOpen(true);
  };

  const handleDeleteProfile = async () => {
    if (!deleteProfile) return;

    const { error } = await supabase
      .from("baby_profiles")
      .delete()
      .eq("id", deleteProfile.id);

    if (error) {
      toast({ title: "Error", description: "Failed to delete profile", variant: "destructive" });
      return;
    }

    toast({ title: "Deleted", description: "Baby profile has been deleted" });
    setDeleteProfile(null);
    loadProfiles();
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Baby Profile</h3>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Baby
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingProfile ? "Edit" : "Create"} Baby Profile</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="babyName">Baby's Name</Label>
                <Input
                  id="babyName"
                  value={babyName}
                  onChange={(e) => setBabyName(e.target.value)}
                  placeholder="Enter name"
                />
              </div>
              <div>
                <Label htmlFor="dueDate">Due Date (if expecting)</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="birthDate">Birth Date (if born)</Label>
                <Input
                  id="birthDate"
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                />
              </div>
              <Button onClick={handleCreateProfile} className="w-full">
                {editingProfile ? "Update Profile" : "Create Profile"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {profiles.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Baby className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>No baby profiles yet. Create one to get started!</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {profiles.map((profile) => (
            <Card
              key={profile.id}
              className="p-4 hover:border-primary transition-colors"
            >
              <div className="flex items-center justify-between">
                <div 
                  className="flex items-center gap-3 flex-1 cursor-pointer"
                  onClick={() => onBabySelect(profile.id)}
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Baby className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold">{profile.baby_name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {profile.birth_date
                        ? `Born ${format(new Date(profile.birth_date), "MMM d, yyyy")}`
                        : profile.due_date
                        ? `Due ${format(new Date(profile.due_date), "MMM d, yyyy")}`
                        : "No date set"}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditProfile(profile);
                    }}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteProfile(profile);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteProfile} onOpenChange={() => setDeleteProfile(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Baby Profile?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {deleteProfile?.baby_name}'s profile and all associated journal entries, milestones, and data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProfile} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
